import { FormEvent } from "react";
import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Camera, ClipboardCheck, Copy, Save, Send } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { FormField } from "@/components/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Task = {
  id: number;
  name: string;
  planned_quantity: string;
  accepted_quantity: string;
  remaining_quantity: string;
  unit: string;
  location: string | null;
  package: string;
  package_reference: string;
  work_type: string | null;
  party: string | null;
  boq_scope: string;
  selected: boolean;
  reported_quantity: string | null;
  notes: string | null;
};
type Sheet = { id: number; sheet_date: string; status: string; blockers: string | null };

function localDate() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export default function TrackSheetForm({
  project,
  sheet,
  copied_from_id,
  tasks,
}: {
  project: { id: number; reference: string; name: string };
  sheet: Sheet | null;
  copied_from_id: number | null;
  tasks: Task[];
}) {
  const form = useForm({
    project_id: project.id.toString(),
    sheet_date: sheet?.sheet_date || localDate(),
    blockers: sheet?.blockers || "",
    copied_from_id: copied_from_id?.toString() || "",
    photos: [] as File[],
    entries: tasks.map((task) => ({
      work_task_id: task.id.toString(),
      included: task.selected,
      reported_quantity: task.reported_quantity || "",
      notes: task.notes || "",
    })),
  });
  const save = () =>
    sheet
      ? form.patch(`/site/track-sheets/${sheet.id}`, { forceFormData: true, preserveScroll: true })
      : form.post("/site/track-sheets", { forceFormData: true });
  const updateRow = (index: number, values: Partial<(typeof form.data.entries)[number]>) =>
    form.setData("entries", form.data.entries.map((row, rowIndex) => rowIndex === index ? { ...row, ...values } : row));

  return (
    <>
      <Head title={`${sheet ? "Edit" : "New"} production track sheet`}>
        <meta name="description" content="Record today's completed quantities against active project micro-jobs." />
        <meta property="og:title" content="Production track sheet" />
        <meta property="og:description" content="Record today's completed quantities against active project micro-jobs." />
      </Head>
      <AppShell>
        <Link href={`/site/today?project_id=${project.id}`} className="inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Today
        </Link>
        <header className="mt-5 border-b border-hairline pb-6">
          <p className="eyebrow">{project.reference} · field production</p>
          <h1 className="mt-3">{sheet ? "Today’s track sheet" : "New track sheet"}</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-muted">Select only work touched today. Enter physical quantity—not a percentage. Submitted facts stay unchanged until a reviewer accepts or returns them.</p>
          {!sheet && (
            <Link href={`/site/track-sheets/new?project_id=${project.id}&copy_yesterday=1`} className="mt-3 inline-flex items-center gap-1 text-sm">
              <Copy className="h-4 w-4" /> Copy yesterday’s task selection
            </Link>
          )}
        </header>
        <form
          className="mt-6 space-y-6"
          onSubmit={(event: FormEvent) => { event.preventDefault(); save(); }}
          onBlurCapture={(event) => {
            if ((event.relatedTarget as HTMLElement | null)?.closest("[data-no-autosave]")) return;
            if (sheet && form.isDirty && !form.processing) {
              form.patch(`/site/track-sheets/${sheet.id}`, {
                forceFormData: true,
                preserveScroll: true,
                preserveState: true,
              });
            }
          }}
        >
          <section className="panel grid gap-4 sm:grid-cols-2">
            <FormField label="Work date" error={form.errors.sheet_date}>
              <Input type="date" max={localDate()} value={form.data.sheet_date} onChange={(event) => form.setData("sheet_date", event.target.value)} />
            </FormField>
            <FormField label="Photos" error={form.errors.photos}>
              <Input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple onChange={(event) => form.setData("photos", Array.from(event.target.files || []))} />
            </FormField>
          </section>
          <section>
            <h2 className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Work completed</h2>
            <p className="mt-1 text-sm text-ink-muted">Large quantity controls use the task’s planned unit and show the remaining measurable scope.</p>
            <div className="mt-4 space-y-3">
              {tasks.map((task, index) => {
                const row = form.data.entries[index];
                return (
                  <article key={task.id} className={`rounded-lg p-4 transition-colors sm:p-5 ${row.included ? "bg-accent-faded ring-1 ring-inset ring-accent/40" : "bg-surface"}`}>
                    <label className="flex min-h-12 items-start gap-3">
                      <input type="checkbox" className="mt-1 h-5 w-5" checked={row.included} onChange={(event) => updateRow(index, { included: event.target.checked })} />
                      <span>
                        <span className="block text-xs font-semibold text-accent-display">{task.package_reference} · {task.location || "Unallocated"}</span>
                        <span className="mt-1 block font-medium text-ink-display">{task.name}</span>
                        <span className="mt-1 block text-xs text-ink-muted">{task.package} · {task.work_type || "Work"} · {task.party || "Unassigned"}</span>
                        {task.boq_scope && <span className="mt-1 block text-xs text-ink-muted">BOQ: {task.boq_scope}</span>}
                      </span>
                    </label>
                    {row.included && (
                      <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(160px,220px)_1fr]">
                        <FormField label={`Completed today (${task.unit})`}>
                          <Input className="min-h-14 text-lg" type="number" inputMode="decimal" min="0.0001" max={task.remaining_quantity} step="0.0001" value={row.reported_quantity} onChange={(event) => updateRow(index, { reported_quantity: event.target.value })} />
                          <p className="mt-1 text-xs text-ink-muted">{task.remaining_quantity} {task.unit} remaining · {task.accepted_quantity} accepted</p>
                        </FormField>
                        <FormField label="Optional note">
                          <Input className="min-h-14" value={row.notes} onChange={(event) => updateRow(index, { notes: event.target.value })} placeholder="Area, issue or evidence note" />
                        </FormField>
                      </div>
                    )}
                  </article>
                );
              })}
              {tasks.length === 0 && <div className="panel py-8 text-center"><p className="font-medium text-ink-display">No production work is ready</p><p className="mt-1 text-sm text-ink-muted">Released, active micro-jobs assigned to you will appear here.</p></div>}
            </div>
          </section>
          <FormField label="Blockers / decisions needed">
            <textarea className="form-control min-h-24" value={form.data.blockers} onChange={(event) => form.setData("blockers", event.target.value)} placeholder="Optional. Keep it short for the reviewer." />
          </FormField>
          <div className="sticky bottom-[max(.75rem,env(safe-area-inset-bottom))] z-20 flex flex-col gap-3 rounded-lg border border-hairline bg-page/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ink-muted"><Camera className="mr-1 inline h-4 w-4" /> {sheet ? "Changes save automatically when you leave a field." : "First save creates today’s autosaved draft."}</p>
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Button className="w-full" data-no-autosave disabled={form.processing}><Save className="h-4 w-4" /> Save draft</Button>
              {sheet && <Button className="w-full" data-no-autosave type="button" disabled={form.processing} onClick={() => form.patch(`/site/track-sheets/${sheet.id}/submit`)}><Send className="h-4 w-4" /> Submit</Button>}
            </div>
          </div>
        </form>
      </AppShell>
    </>
  );
}
